import { useEffect } from "react";
import { applyMode, Mode } from "@amzn/awsui-global-styles";
import { ThemeMode, useSettingsStore } from "src/stores/useSettingsStore";
import {
  PREFERS_DARK_QUERY,
  resolveIsDarkMode,
  systemPrefersDark,
} from "src/util/theme-mode";

/**
 * Applies a theme preference to the page: Cloudscape's dark/light mode plus
 * the native `color-scheme` so scrollbars and form controls match.
 */
export function applyThemeMode(mode: ThemeMode): void {
  const isDark = resolveIsDarkMode(mode, systemPrefersDark());
  applyMode(isDark ? Mode.Dark : Mode.Light);
  document.documentElement.style.colorScheme = isDark ? "dark" : "light";
}

/**
 * Keeps the page theme in sync with the saved preference. While the
 * preference is "system", it also follows live OS light/dark changes.
 */
export function useThemeMode(): void {
  const themeMode = useSettingsStore((state) => state.themeMode);

  useEffect(() => {
    applyThemeMode(themeMode);

    if (themeMode !== "system" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQuery = window.matchMedia(PREFERS_DARK_QUERY);
    const onChange = () => applyThemeMode("system");
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, [themeMode]);
}
